'use client';

import { variables } from './variables';
import { createTheme, Shadows } from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import { createBreakpoints } from '@mui/system';
import sharedTheme from '../shared/theme';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';
import { type OutlinedInput } from '@mui/material';

import { SuccessIcon } from '@/assets/theme/mpanels/alertIcons/SuccessIcon';
import { WarningIcon } from '@/assets/theme/mpanels/alertIcons/WarningIcon';
import { ErrorIcon } from '@/assets/theme/mpanels/alertIcons/ErrorIcon';
import { InfoIcon } from '@/assets/theme/mpanels/alertIcons/InfoIcon';

const { colors, fonts, borderRadius, padding, boxShadows, typography } = variables;

const paperShadow = '0px 11px 16px -16px rgba(35, 25, 63, 0.25)';

const stepperLineBackground = (color: string | undefined) =>
    `50% 50% no-repeat url("data:image/svg+xml,%3Csvg width='35' height='9' viewBox='0 0 35 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='0.5' d='M34.3536 4.85355C34.5488 4.65829 34.5488 4.34171 34.3536 4.14645L31.1716 0.964466C30.9763 0.769204 30.6597 0.769204 30.4645 0.964466C30.2692 1.15973 30.2692 1.47631 30.4645 1.67157L33.2929 4.5L30.4645 7.32843C30.2692 7.52369 30.2692 7.84027 30.4645 8.03553C30.6597 8.2308 30.9763 8.2308 31.1716 8.03553L34.3536 4.85355ZM0 5H34V4H0V5Z' fill='${color}' /%3E%3C/svg%3E%0A")`;

const breakpoints = createBreakpoints({});

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            borderRadius: borderRadius.secondary,
            '.MuiOutlinedInput-notchedOutline, .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: colors.palette.neutral['400'],
                transition: 'all 0.15s ease-in-out',
            },

            '&.Mui-focused, &.Mui-focused:hover': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: colors.other.inputs.focus,
                    boxShadow: boxShadows.inputShadow,
                },
            },

            '&.Mui-disabled': {
                background: colors.palette.neutral['100'],
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                },
                '&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline':
                    {
                        borderColor: 'transparent',
                    },
            },

            '.OTPFieldInput': {
                ...defaultTheme.MuiOutlinedInput.styleOverrides.root['.OTPFieldInput'],
                width: '26px',
                height: '30px',
                marginTop: '-2px',
            },
        },

        notchedOutline: {
            borderRadius: borderRadius.secondary,
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    lineHeight: '140%',
                    '&.homePageWelcomeText': {
                        color: 'white',
                        fontSize: '2.375rem',
                        fontWeight: '600',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px',
                        marginTop: '98px',
                        marginBottom: '32px',
                        b: {
                            color: colors.palette.secondary.main,
                            background: 'white',
                            display: 'inline-flex',
                            padding: '4px 12px',
                        },
                        [breakpoints.up('md')]: {
                            fontSize: '3rem',
                            marginTop: '50px',
                            marginBottom: '72px',
                        },
                    },
                    '&.homePageTitle, &.homePageDescription': {
                        color: 'white',
                    },
                    '&.homePageTitle': {
                        fontSize: '1.5rem',
                        fontWeight: '400',
                    },
                    '&.homePageDescription': {
                        fontSize: '1rem',
                        fontWeight: '400',
                    },
                    '&.nmDashboardWelcomeTitle': {
                        textTransform: 'lowercase',
                        fontSize: '1rem',
                        color: colors.palette.primary.main,
                        '&:first-letter': {
                            textTransform: 'uppercase',
                        },
                    },
                    '&.nmDashboardWelcomeText': {
                        fontSize: '1rem',
                    },
                    '&.nmDashboardNote': {
                        fontSize: '0.75rem',
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.nmSurveyCardInfoTitle': {
                        fontSize: '1rem',
                    },
                    '&.nmSurveyCardLabel': {
                        fontWeight: '400',
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.surveyDetailsValue': {
                        textTransform: 'none',
                        marginRight: '16px',
                        fontWeight: '600',
                        '.currencyText': {
                            color: colors.palette.primary.main,
                        },
                    },
                    '&.dynamicStaticPagesLink': {
                        color: colors.palette.primary.main,
                        fontWeight: '400',
                    },
                    '&.badgeDefinitionTitle, &.introTitle': {
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: colors.palette.secondary['500'],
                    },
                    '&.badgeDefinitionTitle': {
                        color: colors.palette.secondary['800'],
                        margin: '0 0 8px 0',
                    },
                    '&.badgeDefinitionDescription, &.introDescription': {
                        fontSize: '0.875rem',
                        color: colors.palette.neutral['700'],
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                ...defaultTheme.MuiCssBaseline.styleOverrides,
                'a:hover': {
                    textDecoration: 'underline',
                },
                'b, strong': {
                    fontWeight: '600',
                },
                'h1, h2, h3, h4, h5, h6, h7': {
                    fontWeight: '600',
                },
                'h1, h2': {
                    color: colors.palette.secondary.main,
                },
                'h3, h4, h5, h6, h7': {
                    color: colors.palette.primary.main,
                },
                '.introTextBlockWithPic': {
                    [breakpoints.up('md')]: {
                        paddingLeft: '304px',
                        position: 'relative',
                        mb: 2,
                    },
                },
                '&.triangleArrowUp': {
                    borderBottom: `5px solid ${colors.other.text.header}`,
                },
                '&.triangleArrowDown': {
                    borderTop: `5px solid ${colors.other.text.header}`,
                },
                '.topMenuLinksList': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    a: {
                        textDecoration: 'none',
                    },
                },
                '&.verificationFormElementBlock ': {
                    paddingRight: '8px',
                    paddingLeft: '8px',
                },
                '&.verificationSubmitButtonWrapper': {
                    paddingRight: '8px',
                },
                '&.paymentMethodComingSoonLabel': {
                    color: colors.other.text.info,
                    background: 'rgb(229, 246, 253)',
                    padding: '2px 6px',
                    top: '0',
                    right: '20px',
                },
                '&.referralInfoWrapper': {
                    paddingLeft: '8px',
                    paddingTop: '8px',
                },
                '&.WalletMinMaxLabel': {
                    backgroundColor: colors.other.background.highlight,
                    color: colors.other.text.header,
                    borderRadius: borderRadius.tertiary,
                },
                '.HeaderLogoNewYearAnimation': {
                    width: '250px',
                },
                '.AccountUserIdBlock ': {
                    backgroundColor: colors.other.background.header,
                    borderRadius: borderRadius.primary,
                },
                '.AccountUserId': {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.palette.text.primary,
                },
                '&.ForgotPasswordLink': {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                '.profileQuestionsAndSurveys h4': {
                    color: colors.palette.primary['500'],
                },
                '.profileQuestionsAndSurveys a .MuiTypography-root': {
                    color: colors.palette.primary['500'],
                },
                '&.introRegistrationWrapper': {
                    position: 'relative',
                    backgroundImage: 'url("/images/themes/mpanels/registration-intro-bg.jpg")',
                    backgroundSize: '1024px auto',
                    backgroundPosition: '45% top',
                    backgroundRepeat: 'no-repeat',
                    [breakpoints.up('md')]: {
                        backgroundSize: '1440px auto',
                        backgroundPosition: 'center top',
                    },
                },
                '&.introTextBlock': {
                    color: 'white',
                    background: colors.palette.primary.main,
                    padding: '30px 32px',
                },
                '&.MuiPaper-root.infoBox': {
                    border: 'none',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    padding: '8px 0 40px 0',
                },
                '&.MuiPaper-root.nmDashboardWelcomeBlock': {
                    border: `1px solid ${colors.palette.neutral['100']}`,
                },
                '&.MuiPaper-root.nmDashboardRegisterBlock': {
                    border: `1px solid ${colors.palette.neutral['100']}`,
                },
                '&.language-button, &.language-info': {
                    '.SelectedLocale': {
                        display: 'none',
                        fontSize: '1rem',
                        [breakpoints.up('xl')]: {
                            display: 'inline-flex',
                        },
                    },
                    '> .MuiStack-root': {
                        marginRight: '-8px',
                        [breakpoints.up('xl')]: {
                            marginRight: '0',
                        },
                    },
                },
                '&.introStack': {
                    justifyContent: 'space-between',
                    marginTop: '-12px',
                    '.introIconContent': {
                        padding: '8px 0 0 48px',
                    },
                    '.introIcon': {
                        top: '24px',
                        left: '24px',
                        width: '40px',
                        height: '40px',
                        color: colors.palette.secondary['500'],
                        '&.svg-inline--fa': {
                            width: '28px',
                            height: '28px',
                            marginTop: '6px',
                        },
                    },
                    '.homeIntroIconsItem': {
                        flexBasis: '100%',
                        maxWidth: '100%',
                        background: 'white',
                        borderRadius: borderRadius.primary,
                        boxShadow: paperShadow,
                        padding: '24px',
                        margin: '12px 0 0',
                        position: 'relative',
                    },
                    [breakpoints.up('md')]: {
                        marginTop: '-24px',
                        '.homeIntroIconsItem': {
                            flexBasis: 'calc(33.3% - 15px)',
                            maxWidth: 'calc(33.3% - 15px)',
                            marginTop: '24px',
                        },
                    },
                },
                '&.socialbar .socialIconLink': {
                    borderRadius: '0',
                    color: colors.palette.primary[500],
                },
                '&.Header': {
                    '.HeaderPanelImageLogo, .HeaderLogo, .HeaderLogo img': {
                        height: '40px',
                    },
                },
                '&.Footer': {
                    '.followUsTitle': {
                        color: colors.palette.neutral[700],
                    },
                    '.MuiStack-root': {
                        alignItems: 'flex-start',
                    },
                    '.footerMenu': {
                        marginTop: '16px',
                        li: {
                            textAlign: 'left',
                            marginBottom: '8px',
                        },
                    },
                    '.copyright': {
                        textAlign: 'left',
                    },
                    '.FooterLogo': {
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        'a img': {
                            width: '131px',
                            height: '46px',
                        },
                    },
                },
                [breakpoints.up('md')]: {
                    '&.Footer': {
                        '.MuiStack-root': {
                            alignItems: 'flex-start',
                        },
                        '.FooterLogo': {
                            'a img': {
                                width: '92px',
                                height: '32px',
                            },
                        },
                    },
                },
                [breakpoints.up('lg')]: {
                    '&.Footer': {
                        '.FooterLogo': {
                            paddingTop: '0',
                        },
                        '.footerMenu': {
                            marginTop: '0',
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiPaper.styleOverrides.root,
                    '&.lang-switcher-wrapper': {
                        maxHeight: 'calc(100% - 32px)',
                    },
                    '&.introPicBlock': {
                        overflow: 'hidden',
                        padding: 0,
                        lineHeight: 0,
                        marginBottom: '24px',
                        maxHeight: '160px',
                        width: '100%',
                        background: 'transparent',
                        img: {
                            maxWidth: '100%',
                            maxHeight: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                        },
                    },
                    '&.accountTaxDetails': {
                        '.helperText': {
                            position: 'relative',
                            borderRadius: borderRadius.secondary,
                            backgroundColor: colors.palette.background.default,
                            padding: '16px',
                            [breakpoints.up('md')]: {
                                marginLeft: '20px',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '15px',
                                    left: '-9px',
                                    width: '0',
                                    height: '0',
                                    borderTop: '10px solid transparent',
                                    borderBottom: '10px solid transparent',
                                    borderRight: `10px solid ${colors.palette.background.default}`,
                                    transform: 'translateY(-50%)',
                                },
                            },
                        },
                    },
                    '&.dynamicStaticPagesItem': {
                        transition: 'all 0.25s',
                    },
                    '&.dynamicStaticPagesItem:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.accountMenuWrapper, &.accountMenuBox': {
                        padding: '16px',
                    },
                    '&.badgeDefinitions': {
                        display: 'grid',
                        border: 'none',
                        gap: '0',
                        '.badgeDefinitionRow': {
                            display: 'grid',
                            gap: '0',
                            gridAutoFlow: 'column',
                            gridAutoColumns: '1fr',
                            overflowX: 'auto',
                            '.badgeDefinition': {
                                borderRadius: borderRadius.primary,
                                width: '100%',
                            },
                        },
                    },
                },
            },
        },
        MuiPopover: defaultTheme.MuiPopover,
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiBackdrop: defaultTheme.MuiBackdrop,
        MuiCard: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiCard.styleOverrides.root,
                    '&.introRegistration': {
                        ...defaultTheme.MuiCard.styleOverrides.root['&.introRegistration'],
                        background: 'transparent',
                        borderRadius: '0',
                        overflow: 'visible',
                        '&:not(:has(.registrationBox))': {
                            '.MuiAlert-root': {
                                backgroundColor: addOpacityToThemeColor(
                                    colors.palette.secondary['700'],
                                    0.6,
                                ),
                                border: 'none',
                                '.MuiAlert-message': {
                                    color: 'white',
                                },
                            },
                        },
                        [breakpoints.up('lg')]: {
                            '.introTextBlock': {
                                width: '368px',
                                marginRight: '24px',
                                flexShrink: '0',
                            },

                            '.registrationContainer': {
                                width: '100%',
                            },
                        },
                    },
                    '.surveyCardTitle': {
                        color: colors.palette.neutral['800'],
                    },
                    '.surveyDetailsTitle': {
                        color: colors.palette.neutral['500'],
                    },
                    '&.nmSurveyCard': {
                        border: `1px solid ${colors.palette.neutral['100']}`,
                    },
                    '.nmSurveyCardInfoIcon svg path': {
                        fill: colors.palette.neutral['500'],
                    },
                    '.nmSurveyCardTitle': {
                        background: colors.palette.neutral['50'],
                        color: colors.palette.text.primary,
                    },
                    '.nmSurveyCardBaseInfo': {
                        background: colors.palette.neutral['50'],
                        color: colors.palette.neutral['700'],
                    },
                    '&.MuiPaper-root.referralIntroWrapper': {
                        padding: '0',
                        background: 'transparent',
                        boxShadow: 'none',
                        '.referralIntroContainer': {
                            justifyContent: 'space-between',
                            marginTop: '-12px',
                            padding: '0',
                        },
                        '.referralIntroItem': {
                            display: 'inline-flex',
                            flexDirection: 'column',
                            flexBasis: '100%',
                            maxWidth: '100%',
                            background: 'white',
                            borderRadius: borderRadius.primary,
                            boxShadow: paperShadow,
                            padding: '24px',
                            margin: '12px 0 0',
                            position: 'relative',
                        },
                        '.referralIntroIconWrapper': {
                            color: colors.palette.tertiary['500'],
                        },
                        '.referralIntroQuestionNote': {
                            margin: '6px 0 12px 0',
                        },
                        [breakpoints.up('md')]: {
                            '.referralIntroContainer': {
                                marginTop: '-24px',
                            },
                            '.referralIntroItem': {
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                                marginTop: '24px',
                            },
                        },
                    },
                    '&.referralsFiltersWrapper': {
                        padding: '16px 24px',
                        boxShadow: 'none',
                        background: colors.palette.secondary['500'],
                        marginBottom: '12px',
                        '.referralReferText': {
                            color: 'white',
                        },
                        '.referralCopyLink .copyNotification': {
                            color: 'white',
                            background: addOpacityToThemeColor(colors.palette.primary.main, 0.9),
                        },
                    },
                    '&.referralCardWrapper': {
                        padding: '0',
                        '.referralDetails': {
                            background: 'white',
                            paddingBottom: '0',
                            padding: '14px 24px',
                        },
                        '.referralsDetailTitle': {
                            fontSize: '1rem',
                            fontWeight: '600',
                        },
                        '.referralDetailContent li': {
                            marginLeft: '1rem',
                            marginTop: '0.25rem',
                        },
                        '.referralDetailSubtitle': {
                            display: 'inline-flex',
                            fontSize: '0.875rem',
                            color: colors.palette.neutral['500'],
                            marginBottom: '0.25rem',
                        },
                        '.referralActionButtonBlock': {
                            padding: '12px 24px 24px 24px',
                        },
                    },
                    '&.infoCard': {
                        background: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        marginTop: '12px',
                        '.infoCardRow': {
                            justifyContent: 'space-between',
                            flexWrap: 'nowrap',
                            columnGap: '24px',
                        },
                        '.infoCardItem': {
                            marginTop: '24px',
                            flexBasis: '100%',
                            maxWidth: '100%',
                            background: 'white',
                            borderRadius: borderRadius.primary,
                        },
                        [breakpoints.up('sm')]: {
                            '.infoCardItem': {
                                flexBasis: 'calc(50% - 12px)',
                                maxWidth: 'calc(50% - 12px)',
                            },
                        },
                        [breakpoints.up('lg')]: {
                            '.infoCardItem': {
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                            },
                        },
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiChip.styleOverrides.root,
                    fontSize: typography.subtitle3.fontSize,
                    fontWeight: 400,
                    borderRadius: '4px',
                },
            },
        },
        MuiAutocomplete: defaultTheme.MuiAutocomplete,
        MuiSelect: defaultTheme.MuiSelect,
        MuiAccordion: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiAccordion.styleOverrides.root,
                    borderRadius: borderRadius.primary,
                    boxShadow: 'none',
                    '&:first-of-type, &:last-of-type': {
                        borderRadius: borderRadius.primary,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiList.styleOverrides.root,
                    'a:hover': {
                        textDecoration: 'none',
                    },
                    a: {
                        width: '100%',
                        padding: '0 16px',
                    },
                    '&.topNavigationMenu': {
                        border: 'none',
                        backgroundColor: colors.other.background.header,
                        color: colors.palette.text.primary,
                    },
                    '&.topNavigationMenu li': {
                        borderRadius: borderRadius.topMenu,
                        width: '100%',
                    },
                    '&.topNavigationMenu .selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                    },
                },
            },
        },
        MuiListSubheader: defaultTheme.MuiListSubheader,
        MuiMenu: {
            ...defaultTheme.MuiMenu,
            styleOverrides: {
                paper: {
                    border: `1px solid ${colors.palette.neutral['600']}`,
                    borderRadius: borderRadius.secondary,
                    boxShadow: '0px 8px 16px -9px rgba(119, 119, 119, 0.32)',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiMenuItem.styleOverrides.root,
                    '& a': {
                        fontWeight: '400',
                        color: colors.palette.text.primary,
                    },
                    '&.active': {
                        '& a': {
                            backgroundColor: colors.other.background.highlight,
                            boxShadow: `0 4px 16px -11px ${colors.palette.primary.main}`,
                            color: colors.palette.primary.main,
                            fontWeight: '600',
                        },
                    },
                    '&:not(.active):hover': {
                        '& a': {
                            background: colors.palette.background.default,
                            color: colors.palette.primary.main,
                        },
                    },
                    '&.accountMenuItem': {
                        display: 'block',
                        padding: '0',
                        marginBottom: '4px',
                        borderRadius: borderRadius.secondary,
                        background: 'transparent',
                        a: {
                            display: 'block',
                            fontWeight: '400',
                            color: colors.palette.neutral['800'],
                            padding: '9px 16px',
                            borderRadius: borderRadius.secondary,
                            transition: 'all 0.15s',
                            '&:hover': {
                                color: 'white',
                                background: colors.palette.primary['300'],
                            },
                        },
                        '&.active a': {
                            color: 'white',
                            background: colors.palette.primary['500'],
                            '&:hover': {
                                background: colors.palette.primary['300'],
                            },
                        },
                    },
                    '&:hover': {
                        backgroundColor: colors.palette.primary['100'],
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'transparent',
                        color: colors.palette.primary['500'],
                        '&:hover': {
                            backgroundColor: colors.palette.primary['100'],
                        },
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '10px',
                    '&.radioGroupThemeControl': {
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.palette.neutral['100']}`,
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root': {
                        borderRadius: borderRadius.primary,
                    },
                    '&.radioGroupThemeControl:hover': {
                        borderRadius: borderRadius.primary,
                        borderColor: colors.palette.primary['200'],
                        background: addOpacityToThemeColor(colors.palette.primary['100'], 0.2),
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root:hover': {
                        background: 'transparent',
                        boxShadow: 'none',
                    },
                    '&.radioGroupControlActive': {
                        borderColor: colors.palette.primary['200'],
                        background: 'transparent',
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled, &.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled span':
                        {
                            cursor: 'default',
                        },
                    '.ProfileQuestionsContent &:has(:not([value=""]):not(:focus):invalid) .MuiInputLabel-root':
                        {
                            color: colors.other.text.error,
                        },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    color: colors.palette.neutral['500'],
                    '&.Mui-focused, &.Mui-error': {
                        color: colors.palette.neutral['500'],
                    },
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(0, -18px) scale(0.75)',
                    },
                    '&.MuiInputLabel-shrink + .MuiInputBase-root:not(.Mui-focused):not(.Mui-disabled):not(:hover) .MuiOutlinedInput-notchedOutline':
                        {
                            borderColor: colors.palette.neutral['600'],
                        },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: fonts.additional,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                ...defaultTheme.MuiFormControlLabel.styleOverrides,
                label: {
                    ...defaultTheme.MuiFormControlLabel.styleOverrides.label,
                    fontSize: '1rem',
                    paddingTop: '8px',
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                    },
                },
                asterisk: {
                    ...defaultTheme.MuiFormControlLabel.styleOverrides.asterisk,
                    width: '6px',
                    height: '6px',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                ...defaultTheme.MuiCheckbox.styleOverrides,
                root: {
                    ...defaultTheme.MuiCheckbox.styleOverrides.root,
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                ...defaultTheme.MuiCheckbox.styleOverrides,
                root: {
                    ...defaultTheme.MuiRadio.styleOverrides.root,
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                    },
                },
            },
        },
        MuiTouchRipple: defaultTheme.MuiTouchRipple,
        MuiFormHelperText: defaultTheme.MuiFormHelperText,
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: SuccessIcon(),
                    warning: WarningIcon(),
                    error: ErrorIcon(),
                    info: InfoIcon(),
                },
            },
            styleOverrides: {
                icon: {},
                message: {
                    ...defaultTheme.MuiAlert.styleOverrides.message,
                    color: colors.palette.neutral['700'],
                    fontSize: '0.875rem',
                    fontWeight: '400',
                },
                standard: {
                    border: '0 none',
                },
                outlined: {
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: colors.palette.neutral['700'],
                },
                outlinedInfo: {
                    borderColor: colors.other.text.info,
                },
                outlinedSuccess: {
                    borderColor: colors.other.text.success,
                },
                outlinedError: {
                    borderColor: colors.other.text.error,
                },
                outlinedWarning: {
                    borderColor: colors.other.text.warning,
                },
                standardInfo: {
                    backgroundColor: colors.other.background.info,
                },
                filledInfo: {
                    color: colors.other.text.info,
                    backgroundColor: colors.other.background.info,
                },
                standardSuccess: {
                    backgroundColor: colors.other.background.success,
                },
                filledSuccess: {
                    color: colors.other.text.success,
                    backgroundColor: colors.other.background.success,
                },
                standardError: {
                    backgroundColor: colors.other.background.error,
                },
                filledError: {
                    color: colors.other.text.error,
                    backgroundColor: colors.other.background.error,
                },
                standardWarning: {
                    backgroundColor: colors.other.background.warning,
                },
                filledWarning: {
                    color: colors.other.text.warning,
                    backgroundColor: colors.other.background.warning,
                },
                root: {
                    '&.notificationAlert': {
                        border: '0',
                        position: 'relative',
                    },
                    '&.notificationAlertWarning': {
                        backgroundColor: colors.other.background.warning,
                        '.MuiAlert-message': {
                            color: colors.other.text.warning,
                        },
                    },
                    '&.notificationAlertError': {
                        backgroundColor: colors.other.background.error,
                        '.MuiAlert-message': {
                            color: colors.other.text.error,
                        },
                    },
                    '&.notificationAlertInfo': {
                        backgroundColor: colors.other.background.info,
                        '.MuiAlert-message': {
                            color: colors.other.text.info,
                        },
                    },
                    '&.notificationAlertSuccess': {
                        backgroundColor: colors.other.background.success,
                        '.MuiAlert-message': {
                            color: colors.other.text.success,
                        },
                    },
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'tertiary' },
                    style: {
                        color: colors.palette.neutral['800'],
                        backgroundColor: 'transparent',
                        borderRadius: borderRadius.secondary,
                        fontFamily: fonts.secondary,
                        fontSize: '1rem',
                        fontWeight: '400',
                        margin: '0 4px',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.palette.primary['300'],
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                            color: colors.palette.primary.main,
                        },
                        '&:focus': {
                            backgroundColor: 'transparent',
                            color: colors.palette.primary.main,
                        },
                        '& .MuiTouchRipple-root': {
                            display: 'none',
                        },
                        '&.active': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'transparent',
                        },
                    },
                },
                {
                    props: { variant: 'descriptive' },
                    style: {
                        backgroundColor: 'transparent',
                        borderRadius: borderRadius.secondary,
                        border: `1px solid ${colors.palette.neutral['200']}`,
                        padding: '16px',
                        height: 'auto',
                        '& h6': {
                            color: colors.palette.text.primary,
                        },
                        '&:hover': {
                            backgroundColor: colors.palette.warning.main,
                            color: 'white',
                            border: `1px solid ${colors.palette.warning.main}`,
                            '& h6': {
                                color: 'white',
                            },
                        },
                    },
                },
                {
                    props: { variant: 'contained' },
                    style: {
                        '&:hover': {
                            backgroundColor: colors.palette.primary['300'],
                        },
                    },
                },
                {
                    props: { variant: 'text' },
                    style: {
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.palette.primary['300'],
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    borderRadius: borderRadius.secondary,
                    fontFamily: fonts.primary,
                    fontSize: '1rem',
                    height: '38px',
                    fontWeight: '400',
                    textTransform: 'none',
                    '&.language-button[aria-expanded="true"]': {
                        backgroundColor: colors.other.background.highlight,
                    },
                    '&.surveyCardButton': {
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        color: colors.palette.primary['500'],
                        border: `1px solid ${colors.palette.primary['500']}`,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            color: colors.palette.primary['200'],
                            border: `1px solid ${colors.palette.primary['200']}`,
                        },
                        '&:focus': {
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            color: colors.palette.primary['700'],
                            border: `1px solid ${colors.palette.primary['700']}`,
                        },
                    },
                    '&.surveyLoadMoreButton': {
                        width: '100%',
                        backgroundColor: 'transparent',
                        color: colors.palette.secondary['500'],
                        border: `1px solid ${colors.palette.secondary['500']}`,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.palette.secondary['300'],
                            border: `1px solid ${colors.palette.secondary['300']}`,
                        },
                        '&:focus': {
                            backgroundColor: 'transparent',
                            color: colors.palette.secondary['700'],
                            border: `1px solid ${colors.palette.secondary['700']}`,
                        },
                    },
                    '&.controlYourDataButton': {
                        color: colors.palette.primary['500'],
                        fontSize: '1rem',
                        '&:hover': {
                            color: colors.palette.primary['200'],
                        },
                        '&:active': {
                            color: colors.palette.primary['800'],
                        },
                    },
                    '&.dynamicProfilingSubmit': {
                        color: colors.palette.primary['500'],
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.palette.primary['500']}`,
                        '&:hover': {
                            color: colors.palette.primary['200'],
                            backgroundColor: 'transparent',
                            border: `1px solid ${colors.palette.primary['200']}`,
                        },
                        '&:active': {
                            color: colors.palette.primary['800'],
                            backgroundColor: 'transparent',
                            border: `1px solid ${colors.palette.primary['800']}`,
                        },
                    },
                    '&.topMenuButton': {
                        margin: '0 -6px',
                        whiteSpace: 'nowrap',
                        [breakpoints.up('lg')]: {
                            margin: '0 8px',
                        },
                        '&.topMenuButton.selectOpinionTopMenuItem': {
                            display: 'flex',
                            gap: '0.75rem',
                            color: 'white',
                            backgroundColor: 'rgba(29, 81, 86, 1)',
                            ':hover': {
                                backgroundColor: 'rgba(74, 116, 120, 1)',
                            },
                        },
                    },
                },
                outlined: {
                    '&.loginButton, &.mobileLoginButton, &.logoutButton, &.mobileLogoutButton, &.language-button':
                        {
                            fontSize: '1rem',
                            fontWeight: '400',
                        },
                    '&.loginButton, &.mobileLoginButton, &.logoutButton, &.mobileLogoutButton': {
                        height: '38px',
                        borderRadius: borderRadius.secondary,
                        border: '0px',
                        backgroundColor: colors.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                            backgroundColor: colors.palette.primary['300'],
                        },
                    },
                    '&.mobileLoginButton, &.mobileLogoutButton': {
                        color: colors.palette.text.primary,
                        textIndent: '-9999rem',
                        padding: '8px',
                        minWidth: '0',
                        background: 'transparent',
                        '.MuiTouchRipple-root': {
                            display: 'none',
                        },
                        '&:hover': {
                            color: colors.palette.text.primary,
                            background: 'transparent',
                        },
                        '.topmenuButtonIcon': {
                            transform: 'scale(1.55)',
                            marginLeft: '0.5rem',
                        },
                    },
                },
                containedSuccess: {
                    '&.copyLinkButton': {
                        color: 'white',
                        backgroundColor: colors.palette.primary.main,
                        '&:hover': {
                            color: 'white',
                            backgroundColor: colors.palette.primary.main,
                        },
                        '&:active': {
                            color: 'white',
                            backgroundColor: colors.palette.primary.main,
                        },
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                root: {
                    '&.notificationBadge > .MuiBadge-badge': {
                        right: -10,
                        backgroundColor: colors.palette.tertiary['500'],
                    },
                },
            },
        },
        MuiTooltip: defaultTheme.MuiTooltip,
        MuiStep: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStep.styleOverrides.root,
                    color: colors.palette.secondary['500'],
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    '& .Mui-active': {
                        color: colors.palette.secondary['500'],
                    },
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.root,
                    borderRadius: 0,
                    color: 'transparent',
                    width: '1.4em',
                    height: '1.4em',
                    backgroundColor: colors.palette.neutral['50'],
                    '&.Mui-active': {
                        color: colors.palette.secondary['500'],
                        background: colors.palette.secondary['500'],
                        '> text': {
                            fill: 'white',
                        },
                    },
                    '&.Mui-completed': {
                        color: colors.palette.secondary['200'],
                        background: `${colors.palette.secondary['200']} 50% 50% no-repeat url("data:image/svg+xml,%3Csvg fill='white' width='24px' height='24px' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M103.99951,192.00012a7.97346,7.97346,0,0,1-5.65625-2.34277l-56-55.99561a7.99983,7.99983,0,1,1,11.3125-11.31445l50.34375,50.33935L210.34229,66.34778a8.00018,8.00018,0,0,1,11.31445,11.31347l-112,111.9961A7.97937,7.97937,0,0,1,103.99951,192.00012Z'/%3E%3C/svg%3E")`,
                        '> text': {
                            fill: 'white',
                        },
                    },
                },
                text: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.text,
                    fill: colors.palette.neutral['500'],
                    fontWeight: 'normal',
                    '&.Mui-active': {
                        fill: 'white',
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: defaultTheme.MuiStepLabel.styleOverrides.root,
                vertical: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.vertical['.MuiStepLabel-label'],
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.vertical[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: 600,
                            color: colors.palette.secondary['500'],
                        },
                        '&.Mui-completed': {
                            fontWeight: 600,
                            color: colors.palette.secondary['200'],
                        },
                    },
                },
                horizontal: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                            '.MuiStepLabel-label'
                        ],
                        fontWeight: 600,
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: 600,
                            color: colors.palette.secondary['500'],
                        },
                        [breakpoints.up(420)]: {
                            '&.Mui-active': {
                                display: 'inline-block',
                            },
                        },
                        '&.Mui-completed': {
                            fontWeight: 600,
                            color: colors.palette.secondary['200'],
                        },
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepConnector.styleOverrides.root,
                    '&.MuiStepConnector-horizontal .MuiStepConnector-line': {
                        border: '0',
                        height: '10px',
                        background: stepperLineBackground(colors.palette.neutral['200']),
                    },
                    '&.MuiStepConnector-vertical .MuiStepConnector-line': {
                        borderColor: colors.palette.neutral['100'],
                        marginLeft: 5,
                    },
                    '&.Mui-active, &.Mui-completed': {
                        '&.MuiStepConnector-horizontal .MuiStepConnector-line': {
                            background: stepperLineBackground(colors.palette.secondary['200']),
                        },
                        '&.MuiStepConnector-vertical .MuiStepConnector-line': {
                            borderColor: colors.palette.secondary['200'],
                            marginLeft: 5,
                        },
                    },
                },
            },
        },
        MuiDivider: defaultTheme.MuiDivider,
        MuiTabs: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTabs.styleOverrides.root,
                    backgroundColor: 'transparent',
                    borderRadius: borderRadius.secondary,
                    width: '100%',
                    marginBottom: '-1px',
                    [breakpoints.up('sm')]: {
                        '&.MuiTabs-root': {
                            width: 'fit-content',
                        },
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTab.styleOverrides.root,
                    color: colors.palette.text.primary,
                    minHeight: '42px',
                    padding: '12px 32px 8px 32px',
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderBottom: '2px solid transparent',
                    [breakpoints.up('sm')]: {
                        '&.MuiButtonBase-root': {
                            flexBasis: 'auto',
                        },
                    },
                    '&:hover': {
                        backgroundColor: colors.palette.primary['100'],
                        color: colors.palette.primary['500'],
                    },
                    '&:active': {
                        backgroundColor: colors.palette.primary['500'],
                        color: 'white',
                    },
                    '&.Mui-selected': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&.Mui-selected'],
                        borderRadius: variables.borderRadius.secondary,
                        borderBottomColor: colors.palette.primary.main,
                        color: colors.palette.primary.main,
                        backgroundColor: 'transparent',
                    },
                },
            },
        },
        MuiTabPanel: {
            ...defaultTheme.MuiTabPanel,
            styleOverrides: {
                root: {
                    padding: '0px',
                    paddingTop: '21px',
                    borderTop: `1px solid ${colors.palette.neutral['200']}`,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                ...defaultTheme.MuiTableHead.styleOverrides,
                root: {
                    ...defaultTheme.MuiTableHead.styleOverrides.root,
                    fontWeight: '400',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                ...defaultTheme.MuiTableCell.styleOverrides,
                head: {
                    ...defaultTheme.MuiTableCell.styleOverrides.head,
                    fontWeight: '400',
                    borderBottomColor: colors.palette.neutral['200'],
                },
            },
        },
        MuiTableRow: defaultTheme.MuiTableRow,
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    background: 'none',
                    borderRadius: 0,
                    color: colors.palette.neutral['800'],
                    '&:hover': {
                        backgroundColor: colors.palette.primary['300'],
                        color: 'white',
                    },
                    '&:active': {
                        backgroundColor: colors.palette.primary['800'],
                        color: 'white',
                    },
                    '&.Mui-selected': {
                        backgroundColor: colors.palette.primary['500'],
                        color: 'white',
                        '&:hover': {
                            backgroundColor: colors.palette.primary['300'],
                        },
                    },
                    '&.Mui-disabled': {
                        backgroundColor: colors.palette.neutral['100'],
                        color: colors.palette.neutral['500'],
                    },
                    '&.MuiPaginationItem-previousNext': {
                        background: 'none',
                        '&:hover': {
                            background: 'none',
                            color: colors.palette.primary['200'],
                        },
                        '&:active': {
                            background: 'none',
                            color: colors.palette.primary['800'],
                        },
                    },
                },
            },
        },
        MuiPickersPopper: defaultTheme.MuiPickersPopper,
        MuiPickersDay: defaultTheme.MuiPickersDay,
        MuiSkeleton: defaultTheme.MuiSkeleton,
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '.MuiSwitch-thumb': {
                        backgroundColor: 'white',
                        filter: 'drop-shadow(0px 2px 1px rgba(119, 119, 119, 0.20))',
                    },
                    '.MuiSwitch-switchBase + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['500'],
                        opacity: 0.38,
                    },
                    '.Mui-checked .MuiSwitch-thumb': {
                        backgroundColor: colors.palette.primary['500'],
                    },
                    '.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.palette.primary['500'],
                        opacity: 0.5,
                    },
                    '.Mui-disabled .MuiSwitch-thumb': {
                        backgroundColor: colors.palette.neutral['100'],
                    },
                    '.MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['300'],
                        opacity: 1,
                    },
                },
            },
        },
    },
    shadows: ['none', ...(Array(24).fill(paperShadow) as string[])] as Shadows,
});

type CSSProperties = React.CSSProperties & {
    [key: string]: CSSProperties | string | number | undefined;
};

declare module '@mui/material/styles' {
    interface Theme {
        colors: typeof colors;
        fonts: typeof fonts;
        borderRadius: typeof borderRadius;
        padding: typeof padding;
    }
    interface ThemeOptions {
        colors: typeof colors;
        fonts: typeof fonts;
        borderRadius: typeof borderRadius;
        padding: typeof padding;
    }

    interface TypographyVariants {
        subtitle3: React.CSSProperties;
        greyLabel: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        subtitle3: React.CSSProperties;
        greyLabel: React.CSSProperties;
    }

    interface Components {
        MuiPickersInputBase?: {
            defaultProps?: {};
            styleOverrides?: {
                root?: CSSProperties;
            };
        };
        MuiPickersDay?: {
            defaultProps?: React.ComponentProps<typeof PickersDay>;
            styleOverrides?: {
                root?: CSSProperties;
            };
        };
        MuiPickersPopper?: {
            defaultProps?: React.ComponentProps<typeof DateCalendar>;
            styleOverrides?: {
                paper?: CSSProperties;
                root?: CSSProperties;
            };
        };
        MuiPickersOutlinedInput?: {
            defaultProps?: React.ComponentProps<typeof OutlinedInput>;
            styleOverrides?: {
                root?: CSSProperties;
                paper?: CSSProperties;
            };
        };
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        subtitle3: true;
        greyLabel: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        tertiary: true;
        descriptive: true;
    }
}

export default theme;
